import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { reduzirImagem } from '@/lib/imagem';
import Modal from '@/components/ui/Modal';
import type { Premio } from './types';

interface Props {
  rifaId: number;
  premio: Premio | null; // null = novo prêmio
  proximaOrdem: number;
  aoFechar: () => void;
  aoSalvo: (lista: Premio[]) => void;
}

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 5 * 1024 * 1024;

function ordinal(n: number): string {
  return `${n}º`;
}

export default function ModalPremio({ rifaId, premio, proximaOrdem, aoFechar, aoSalvo }: Props) {
  const [descricao, setDescricao] = useState(premio?.descricao ?? '');
  const [doadoPor, setDoadoPor] = useState(premio?.doado_por ?? '');
  // imagemAcao: null = não mexe · '' = limpar · url = trocar.
  const [imagemAcao, setImagemAcao] = useState<string | null>(null);

  const [enviando, setEnviando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroTecnico, setErroTecnico] = useState<string | null>(null);
  const [confirmarRemover, setConfirmarRemover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Foto exibida agora: a escolhida (ou limpa) vale; senão a já salva.
  const imagemAtual = imagemAcao === '' ? null : imagemAcao ?? premio?.imagem_url ?? null;

  const temFotoAtual = !!imagemAtual;
  const sorteado = premio?.sorteado === true;

  async function copiarErro() {
    if (!erroTecnico) return;
    try {
      await navigator.clipboard.writeText(erroTecnico);
    } catch {
      /* ignora */
    }
  }

  async function aoEscolherFoto(arquivo: File | null) {
    setErro(null);
    setErroTecnico(null);
    if (!arquivo) return;
    if (!EXTENSOES_OK.includes(arquivo.type)) {
      setErro('Use uma imagem jpg, png ou webp.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      setErro('Esta imagem tem mais de 5 MB. Escolha uma menor.');
      return;
    }
    setEnviando(true);
    try {
      const reduzido = await reduzirImagem(arquivo, 800, 0.85, 'image/webp');
      const ordem = premio?.ordem ?? proximaOrdem;
      const caminho = `rifas/${rifaId}/premio-${ordem}-${Date.now()}.webp`;
      const { error: errUpload } = await supabase.storage.from('capas').upload(caminho, reduzido);
      if (errUpload) {
        setErroTecnico(mensagemAmigavel(errUpload, 'Erro ao enviar a foto.'));
        return;
      }
      const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
      setImagemAcao(data.publicUrl);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function limparFoto() {
    setImagemAcao('');
  }

  async function salvar() {
    if (!descricao.trim()) {
      setErro('Escreva o que é o prêmio.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('rifa_salvar_premio', {
        p_rifa: rifaId,
        p_descricao: descricao.trim(),
        p_premio: premio?.premio_id ?? null,
        p_doado_por: doadoPor.trim(),
        p_imagem_url: imagemAcao,
        p_ordem: premio ? null : proximaOrdem,
      });
      if (error) throw error;
      aoSalvo((data as Premio[]) || []);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível salvar o prêmio.'));
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!premio) return;
    setRemovendo(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('rifa_remover_premio', {
        p_rifa: rifaId,
        p_premio: premio.premio_id,
      });
      if (error) throw error;
      aoSalvo((data as Premio[]) || []);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível apagar o prêmio.'));
      setConfirmarRemover(false);
    } finally {
      setRemovendo(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={premio ? `Editar ${ordinal(premio.ordem)} prêmio` : 'Adicionar prêmio'}
      aoFechar={aoFechar}
      rodape={
        <div className="flex flex-col gap-2 sm:flex-row">
          {premio && !sorteado && (
            <button
              type="button"
              onClick={() => setConfirmarRemover(true)}
              disabled={salvando || removendo}
              className="flex min-h-[48px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-danger-200 bg-white px-4 text-sm font-semibold text-danger-600 transition-colors hover:bg-danger-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-delete-bin-line" />
              </span>
              Remover
            </button>
          )}
          <button
            type="button"
            onClick={salvar}
            disabled={salvando || removendo || !descricao.trim()}
            className="flex min-h-[48px] flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-6 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {salvando ? (
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
            ) : (
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-check-line" />
              </span>
            )}
            Salvar
          </button>
        </div>
      }
    >
      {confirmarRemover ? (
        <div className="py-4 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 text-danger-600">
            <i className="ri-error-warning-line text-2xl" />
          </span>
          <h3 className="mt-3 text-[16px] font-bold text-foreground-950">Apagar este prêmio?</h3>
          <p className="mt-1 text-sm text-foreground-500">
            Isso não pode ser desfeito.
          </p>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setConfirmarRemover(false)}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={remover}
              disabled={removendo}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-2xl bg-danger-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-danger-700 disabled:opacity-50"
            >
              {removendo ? 'Apagando…' : 'Apagar'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Foto */}
          <div>
            <span className="text-sm font-medium text-foreground-900">Foto do prêmio</span>
            <div className="mt-2 flex items-start gap-3">
              <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-secondary-50">
                {temFotoAtual ? (
                  <img
                    src={imagemAtual!}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="flex flex-col items-center justify-center text-secondary-400">
                    <span className="flex h-6 w-6 items-center justify-center">
                      <i className="ri-gift-line text-2xl" />
                    </span>
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={enviando}
                  className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl border border-secondary-300 bg-white px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-camera-line" />
                  </span>
                  {temFotoAtual ? 'Trocar foto' : 'Enviar foto'}
                </button>
                {temFotoAtual && (
                  <button
                    type="button"
                    onClick={limparFoto}
                    disabled={enviando}
                    className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl border border-secondary-300 bg-white px-3 text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-delete-bin-line" />
                    </span>
                    Remover foto
                  </button>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  className="sr-only"
                  onChange={(e) => aoEscolherFoto(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            {enviando && (
              <p className="mt-2 flex items-center gap-2 text-sm text-secondary-500">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" />
                </span>
                Salvando foto…
              </p>
            )}
            {erroTecnico && (
              <div className="mt-3 rounded-xl border border-danger-200 bg-danger-50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-danger-700">
                      Não foi possível salvar a foto.
                    </p>
                    <p className="mt-1 break-words text-sm text-danger-700">{erroTecnico}</p>
                  </div>
                  <button
                    type="button"
                    onClick={copiarErro}
                    className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg border border-danger-300 bg-white px-2 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
                  >
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-file-copy-line" />
                    </span>
                    Copiar erro
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* O que é */}
          <div>
            <label htmlFor="premio-descricao" className="text-sm font-medium text-foreground-900">
              O que é o prêmio
            </label>
            <textarea
              id="premio-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
              maxLength={140}
              placeholder="Ex.: Agenda personalizada do acampamento"
              className="mt-2 w-full resize-none rounded-xl border border-secondary-300 bg-white px-3 py-2.5 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>

          {/* Doado por */}
          <div>
            <label htmlFor="premio-doado" className="text-sm font-medium text-foreground-900">
              Doado por <span className="font-normal text-foreground-400">(opcional)</span>
            </label>
            <input
              id="premio-doado"
              type="text"
              value={doadoPor}
              onChange={(e) => setDoadoPor(e.target.value)}
              maxLength={80}
              placeholder="Nome de quem doou"
              className="mt-2 w-full rounded-xl border border-secondary-300 bg-white px-3 py-2.5 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <p className="mt-1 text-xs text-foreground-500">
              Aparece na cartela e na página de compra.
            </p>
          </div>

          {erro && (
            <p className="rounded-xl bg-danger-50 px-3 py-2.5 text-sm text-danger-700" role="alert">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}